import React, { useEffect, useRef, useState } from "react";

export const EXAMPLES = [
  {
    id: "caregiving-help",
    text: "Mom, I need more help caring for you.",
    icon: "heart",
  },
  {
    id: "relationship-distance",
    text: "I miss how close we used to be. Did I do something?",
    icon: "connection",
  },
  {
    id: "childhood",
    text: "What was your childhood really like?",
    icon: "home",
  },
  {
    id: "money",
    text: "I’m scared to bring up money with my dad.",
    icon: "wallet",
  },
  {
    id: "driving-safety",
    text: "I’m worried driving isn't safe for you anymore.",
    icon: "car",
  },
  {
    id: "boundaries",
    text: "Why do you get upset when I'm trying to help?",
    icon: "shield",
  },
];

function ExampleIcon({ name }) {
  const commonProps = {
    "aria-hidden": "true",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className: "h-5 w-5",
  };

  if (name === "heart") {
    return (
      <svg {...commonProps}>
        <path d="M20.8 4.6a5.2 5.2 0 0 0-7.4 0L12 6l-1.4-1.4a5.2 5.2 0 1 0-7.4 7.4L12 20.8 20.8 12a5.2 5.2 0 0 0 0-7.4Z" />
      </svg>
    );
  }

  if (name === "connection") {
    return (
      <svg {...commonProps}>
        <path d="M8.5 12.5 6.8 14.2a3 3 0 0 0 4.2 4.2l2.1-2.1" />
        <path d="m10.9 7.7 2.1-2.1a3 3 0 0 1 4.2 4.2l-1.7 1.7" />
        <path d="m8.8 15.2 6.4-6.4" />
      </svg>
    );
  }

  if (name === "home") {
    return (
      <svg {...commonProps}>
        <path d="m3.5 10.5 8.5-7 8.5 7" />
        <path d="M5.5 9v11h13V9M9.5 20v-6h5v6" />
      </svg>
    );
  }

  if (name === "car") {
    return (
      <svg {...commonProps}>
        <path d="M5 13.5 6.5 8a2 2 0 0 1 1.9-1.5h7.2A2 2 0 0 1 17.5 8l1.5 5.5" />
        <path d="M4 13.5h16v4a1.5 1.5 0 0 1-1.5 1.5H17a1.5 1.5 0 0 1-1.5-1.5v-.5h-7v.5A1.5 1.5 0 0 1 7 19H5.5A1.5 1.5 0 0 1 4 17.5v-4Z" />
        <path d="M7 14.5h.1" />
        <path d="M17 14.5h.1" />
      </svg>
    );
  }

  if (name === "shield") {
    return (
      <svg {...commonProps}>
        <path d="M12 3.5 19 6v5.2c0 4.3-2.8 7.6-7 9.3-4.2-1.7-7-5-7-9.3V6l7-2.5Z" />
        <path d="M12 8.2v5" />
        <path d="M12 16.2h.01" />
      </svg>
    );
  }

  if (name === "wallet") {
    return (
      <svg {...commonProps}>
        <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18a2 2 0 0 1 2 2v10.5a2 2 0 0 1-2 2H6.5A2.5 2.5 0 0 1 4 17V7.5Z" />
        <path d="M4 8h14.5A1.5 1.5 0 0 1 20 9.5V11h-4a2.5 2.5 0 0 0 0 5h4" />
        <path d="M16 13.5h.01" />
      </svg>
    );
  }

  return null;
}

export default function RefineStartingExamples({
  onSelectExample,
}) {
  const [selectedId, setSelectedId] = useState("");
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [hasMoreExamples, setHasMoreExamples] = useState(false);
  const clearSelectedTimeoutRef = useRef(null);
  const examplesScrollRef = useRef(null);

  useEffect(() => {
    return () => {
      if (clearSelectedTimeoutRef.current) {
        clearTimeout(clearSelectedTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const scrollContainer = examplesScrollRef.current;

    if (!scrollContainer) {
      return undefined;
    }

    const updateHasMoreExamples = () => {
      setCanScrollLeft(scrollContainer.scrollLeft > 1);
      setHasMoreExamples(
        scrollContainer.scrollLeft + scrollContainer.clientWidth <
          scrollContainer.scrollWidth - 1
      );
    };

    updateHasMoreExamples();
    scrollContainer.addEventListener("scroll", updateHasMoreExamples, {
      passive: true,
    });
    window.addEventListener("resize", updateHasMoreExamples);

    return () => {
      scrollContainer.removeEventListener(
        "scroll",
        updateHasMoreExamples
      );
      window.removeEventListener("resize", updateHasMoreExamples);
    };
  }, []);

  const selectExample = (example) => {
    onSelectExample(example.text);
    setSelectedId(example.id);

    if (clearSelectedTimeoutRef.current) {
      clearTimeout(clearSelectedTimeoutRef.current);
    }

    clearSelectedTimeoutRef.current = setTimeout(() => {
      setSelectedId("");
    }, 900);
  };

  const revealMoreExamples = () => {
    const scrollContainer = examplesScrollRef.current;

    if (!scrollContainer) {
      return;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    scrollContainer.scrollBy({
      left: scrollContainer.clientWidth * 0.8,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  const revealPreviousExamples = () => {
    const scrollContainer = examplesScrollRef.current;

    if (!scrollContainer) {
      return;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    scrollContainer.scrollBy({
      left: scrollContainer.clientWidth * -0.8,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  return (
    <div className="!mt-3">
      <style>{`
        .refine-starting-examples-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
          scroll-padding-inline: 0.25rem;
        }

        .refine-starting-examples-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <p className="px-1 text-xs font-semibold leading-none text-white/66">
        Need a starting point?
      </p>

      <div className="relative">
        <div
          ref={examplesScrollRef}
          className="refine-starting-examples-scroll mt-2 flex min-w-0 snap-x snap-mandatory gap-2.5 overflow-x-auto overscroll-x-contain px-1 pb-1"
          aria-label="Starting examples"
        >
          {EXAMPLES.map((example) => {
          const isSelected = selectedId === example.id;

          return (
            <button
              key={example.id}
              type="button"
              aria-label={`Use example: ${example.text}`}
              aria-pressed={isSelected}
              onClick={() => selectExample(example)}
              className={[
                "relative flex min-h-[88px] w-[168px] shrink-0 snap-start flex-col justify-center rounded-[0.7rem] border px-3 py-2 text-left text-white/88 outline-none backdrop-blur-md transition duration-200 sm:w-[184px]",
                "bg-[#0d0f09]/66 shadow-[inset_0_1px_0_rgba(255,255,255,0.045),0_10px_22px_rgba(0,0,0,0.14),0_0_14px_rgba(244,217,78,0.045)]",
                "border-[#f4d94e]/18 hover:border-[#f4d94e]/34 hover:bg-[#12140d]/76 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_12px_24px_rgba(0,0,0,0.16),0_0_18px_rgba(244,217,78,0.075)]",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#fff8df]/70",
                isSelected
                  ? "border-[#f4d94e]/46 bg-[#17160c]/82 ring-1 ring-[#f4d94e]/18"
                  : "",
              ].join(" ")}
            >
              <span className="flex items-start gap-2.5">
                <span className="mt-0.5 shrink-0 text-[#f4d94e]">
                  <ExampleIcon name={example.icon} />
                </span>

                <span className="line-clamp-3 min-w-0 flex-1 text-xs font-medium leading-snug tracking-normal text-white/90">
                  {example.text}
                </span>

                {isSelected ? (
                  <span
                    aria-hidden="true"
                    className="grid h-5 w-5 shrink-0 place-items-center rounded-full border border-[#f4d94e]/70 bg-[#f4d94e]/12 text-[#f4d94e]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3 w-3"
                    >
                      <path d="m3.5 8.2 2.6 2.6 6.4-6.4" />
                    </svg>
                  </span>
                ) : null}
              </span>
            </button>
          );
          })}
        </div>

        <button
          type="button"
          aria-label="Show previous starting examples"
          onClick={revealPreviousExamples}
          disabled={!canScrollLeft}
          className="absolute left-0 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 place-items-center rounded-full border border-[#f4d94e]/30 bg-[#0d0f09]/90 text-[#fff6a8] shadow-[0_8px_20px_rgba(0,0,0,0.28),0_0_14px_rgba(244,217,78,0.08)] backdrop-blur-sm transition hover:border-[#f4d94e]/55 hover:bg-[#17160c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#fff8df]/70 disabled:pointer-events-none disabled:opacity-0 sm:grid"
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m12.5 4.5-5 5.5 5 5.5" />
          </svg>
        </button>

        <button
          type="button"
          aria-label="Show more starting examples"
          onClick={revealMoreExamples}
          disabled={!hasMoreExamples}
          className="absolute right-0 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 place-items-center rounded-full border border-[#f4d94e]/30 bg-[#0d0f09]/90 text-[#fff6a8] shadow-[0_8px_20px_rgba(0,0,0,0.28),0_0_14px_rgba(244,217,78,0.08)] backdrop-blur-sm transition hover:border-[#f4d94e]/55 hover:bg-[#17160c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#fff8df]/70 disabled:pointer-events-none disabled:opacity-0 sm:grid"
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m7.5 4.5 5 5.5-5 5.5" />
          </svg>
        </button>
      </div>
    </div>
  );
}
